import type {
  BatteryFinancingSelectionStatus,
  BatteryProposalDto,
  BatteryProposalFinancingDto,
  BatteryQuoteDto,
  BatterySelectedLoanProductDto,
} from "~/api/dto/battery-proposal.dto"

import { isBatteryProposalReadOnly } from "../battery-proposal-read-only"
import type { ResolvedContractAction } from "../contract/contract-action"
import { resolveContractAction } from "../contract/contract-action"
import { CONCERT_BATTERY_FINANCE_TYPE } from "../financing-status/concert/concert-battery-copy"
import {
  canShowBatteryReserveNow,
  financingHasRetainedLoanApplication,
} from "../financing-status/financing-status-experiences"
import { LIGHTREACH_BATTERY_FINANCE_TYPE } from "../lightreach-battery"
import { SUNGAGE_BATTERY_FINANCE_TYPE } from "../sungage/sungage-battery-copy"

/** Credit Human application entry: Application status, or Apply for financing. */
export type FinancialSummaryApplicationAction =
  | { kind: `application-status` }
  | { kind: `apply`; quoteId: string }

/** The inline contract Reserve now link. */
export type FinancialSummaryContractReserveAction = {
  kind: `contract-reserve`
  contractAction: ResolvedContractAction
}

export type FinancialSummaryLightReachAction = {
  kind: `lightreach`
  financing: BatteryProposalFinancingDto
  needsLoanProduct: boolean
}

export type FinancialSummaryNoAction = { kind: `none` }

export type FinancialSummaryConcertAction = {
  kind: `concert`
  quote: BatteryQuoteDto | null
  needsLoanProduct: boolean
}

export type FinancialSummarySungageAction = {
  kind: `sungage`
  financing: BatteryProposalFinancingDto
  needsLoanProduct: boolean
}

export type FinancialSummarySelectLoanProductAction = {
  kind: `select-loan-product`
}

/** What the summary-error fallback can offer: no Concert or Sungage entry. */
export type FinancialSummaryFallbackAction =
  | FinancialSummaryApplicationAction
  | FinancialSummaryContractReserveAction
  | FinancialSummaryLightReachAction
  | FinancialSummaryNoAction

/** The one call to action beneath the Battery-Only financial summary ledger. */
export type FinancialSummaryAction =
  | FinancialSummaryConcertAction
  | FinancialSummaryFallbackAction
  | FinancialSummarySelectLoanProductAction
  | FinancialSummarySungageAction

export type FinancialSummaryActionInput = {
  proposal: BatteryProposalDto
  /** The financing read; null while it loads or after it fails. */
  financing: BatteryProposalFinancingDto | null
}

/**
 * True when the seller's current selection can be priced from setup. An
 * inactive / unavailable / out-of-limits product, or an empty selection, must
 * not fall back to the Quote's old Loan Product id — those cases open Loan
 * Product instead.
 */
export const eligibleSelectedLoanProductId = ({
  selectedLoanProduct,
  selectionStatus,
}: {
  selectedLoanProduct: BatterySelectedLoanProductDto | null
  selectionStatus: BatteryFinancingSelectionStatus | null
}): string | null => {
  if (selectedLoanProduct == null || selectionStatus == null) {
    return null
  }
  if (
    selectedLoanProduct.availability !== `eligible` ||
    selectionStatus === `selected_product_unavailable`
  ) {
    return null
  }
  return selectedLoanProduct.id
}

/**
 * LightReach offers never travel on the generic eligible list, so the server
 * withholds the generic selection action. Refresh Pricing never re-quotes a
 * LightReach offer either, so the summary sends the seller to the picker
 * until a current offer is saved.
 */
const lightReachNeedsOffer = (
  financing: BatteryProposalFinancingDto | null,
  proposal: BatteryProposalDto,
): boolean =>
  financing != null &&
  (financing.selectedLoanProduct == null ||
    financing.quote?.status === `stale`) &&
  financing.financingLocked !== true &&
  !isBatteryProposalReadOnly(proposal)

/** The stored Finance Plan; the financing read backs it up. */
const summaryFinanceType = ({
  proposal,
  financing,
}: FinancialSummaryActionInput): string | null =>
  proposal.financePlan?.financeType ??
  financing?.financePlan?.financeType ??
  null

/**
 * Before an Existing Loan Application exists, Apply for financing is the entry
 * while the server allows it and a Quote exists. After any application attempt
 * exists, only Application status remains.
 */
const applicationAction = (
  financing: BatteryProposalFinancingDto,
): FinancialSummaryApplicationAction | null => {
  if (financingHasRetainedLoanApplication(financing)) {
    return { kind: `application-status` }
  }
  const quoteId = financing.quote?.id ?? null
  if (financing.actions.apply.allowed && quoteId != null) {
    return { kind: `apply`, quoteId }
  }
  return null
}

/**
 * Reserve now stays beneath the ledger only when financing is known, has no
 * Existing Loan Application, and Apply is not available. After a Contract
 * exists the proposal page shows no Contract entry — Contract status lives
 * in the Setup menu.
 */
const contractReserveAction = (
  proposal: BatteryProposalDto,
  financing: BatteryProposalFinancingDto,
): FinancialSummaryContractReserveAction | null => {
  const resolved = resolveContractAction(proposal)
  return resolved?.reserve === true && canShowBatteryReserveNow(financing)
    ? { kind: `contract-reserve`, contractAction: resolved }
    : null
}

/** The call to action beneath a stored financial summary ledger. */
export const financialSummaryAction = (
  input: FinancialSummaryActionInput,
): FinancialSummaryAction => {
  const { proposal, financing } = input
  const financeType = summaryFinanceType(input)
  const isLightReach = financeType === LIGHTREACH_BATTERY_FINANCE_TYPE
  const needsLoanProduct = isLightReach
    ? lightReachNeedsOffer(financing, proposal)
    : (financing?.actions.selectLoanProduct.allowed ?? false) &&
      eligibleSelectedLoanProductId({
        selectedLoanProduct: financing?.selectedLoanProduct ?? null,
        selectionStatus: financing?.selectionStatus ?? null,
      }) == null

  // Concert Battery applies in Vision: Reserve now initiates the customer and
  // opens the in-person application; status lives under Contract → Status.
  if (financeType === CONCERT_BATTERY_FINANCE_TYPE) {
    return { kind: `concert`, quote: financing?.quote ?? null, needsLoanProduct }
  }
  if (financeType === SUNGAGE_BATTERY_FINANCE_TYPE) {
    return financing != null
      ? { kind: `sungage`, financing, needsLoanProduct }
      : { kind: `none` }
  }
  if (isLightReach) {
    // LightReach applies in Vision: Reserve now opens the credit
    // application; status lives in the financing-status drawer.
    return financing != null
      ? { kind: `lightreach`, financing, needsLoanProduct }
      : { kind: `none` }
  }
  // A loading or failed financing read is not proof that no application or
  // Apply entry exists, so neither it nor Reserve now shows.
  if (financing == null) {
    return { kind: `none` }
  }
  if (needsLoanProduct && !financingHasRetainedLoanApplication(financing)) {
    // One action at a time: until a Loan Product is chosen, neither
    // Apply nor Reserve now can act on it.
    return { kind: `select-loan-product` }
  }
  return (
    applicationAction(financing) ??
    contractReserveAction(proposal, financing) ?? { kind: `none` }
  )
}

/**
 * A summary-endpoint failure must not hide the one financing action when
 * financing is known. Application status and Apply both live here; Reserve
 * now is only the remaining no-apply path. A loading or failed financing
 * read is not proof that none of those exist; contract access stays in Setup.
 */
export const financialSummaryFallbackAction = (
  input: FinancialSummaryActionInput,
): FinancialSummaryFallbackAction => {
  const { proposal, financing } = input
  if (financing == null) {
    return { kind: `none` }
  }
  // LightReach applies in Vision and has no contract Reserve now, so its
  // entry is the one financing action to keep.
  if (summaryFinanceType(input) === LIGHTREACH_BATTERY_FINANCE_TYPE) {
    return {
      kind: `lightreach`,
      financing,
      needsLoanProduct: lightReachNeedsOffer(financing, proposal),
    }
  }
  return (
    applicationAction(financing) ??
    contractReserveAction(proposal, financing) ?? { kind: `none` }
  )
}
